import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { FontAwesome5 } from "@expo/vector-icons"
import React, { useMemo, useState } from "react"
import { TouchableOpacity, View } from "react-native"

const COLLAPSED_VISIBLE_CHIPS = 4
const CHIP_MAX_WIDTH = 360
// Generation always produces a batch of 3 cards, and the AI input schema caps
// chapter_skills at 3 (skills_part indexes 1..3). Selecting a 4th skill has no
// card to land on and is rejected by the AI (422). Cap the selection here so
// the tutor sees the limit instead of hitting a silent failure. The backend
// clamps too — this is the UX half of a defense-in-depth pair.
const MAX_SELECTABLE_SKILLS = 3

/**
 * "Compétences travaillées" chip picker shown below the Chapter selector in
 * GenerateActivityModal. Selected chips fill with the primary color and show
 * a check icon; unselected chips use a muted grey palette so the active
 * selection is unambiguous at a glance. When the chapter has more than 4
 * skills, the list is collapsed behind a "Voir plus" toggle (auto-expanded
 * whenever a selected chip would otherwise be hidden).
 *
 * Selection is capped at `maxSelectable` (default 3): once reached, unselected
 * chips are disabled (deselecting stays possible) and a hint is shown.
 */
export default function SkillPartsChipPicker({
    skillParts = [],
    selectedPositions = [],
    onTogglePosition,
    maxSelectable = MAX_SELECTABLE_SKILLS,
}) {
    const [expanded, setExpanded] = useState(false)
    const selectionFull = selectedPositions.length >= maxSelectable

    const hasSelectedBeyondCap = useMemo(
        () =>
            skillParts
                .slice(COLLAPSED_VISIBLE_CHIPS)
                .some((sp) => selectedPositions.includes(sp.position)),
        [skillParts, selectedPositions]
    )

    if (!skillParts || skillParts.length === 0) {
        return null
    }

    const isExpanded = expanded || hasSelectedBeyondCap
    const visibleSkillParts = isExpanded
        ? skillParts
        : skillParts.slice(0, COLLAPSED_VISIBLE_CHIPS)
    const hiddenCount = skillParts.length - visibleSkillParts.length
    const showToggle =
        skillParts.length > COLLAPSED_VISIBLE_CHIPS && !hasSelectedBeyondCap

    return (
        <View style={{ marginBottom: 15 }}>
            <TextM
                color={theme.colors.primary}
                fontWeight="Medium"
                fontSize={12}
                style={{ marginBottom: selectionFull ? 4 : 10 }}
            >
                Compétences travaillées (max {maxSelectable})
            </TextM>
            {selectionFull && (
                <TextM
                    color={theme.colors.grey}
                    fontWeight="Light"
                    fontSize={10}
                    style={{ marginBottom: 10, fontStyle: "italic" }}
                >
                    Désélectionne pour changer de compétence.
                </TextM>
            )}
            <View
                style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 8,
                    alignItems: "center",
                }}
            >
                {visibleSkillParts.map((sp) => {
                    const isSelected = selectedPositions.includes(sp.position)
                    // At the cap, only already-selected chips stay interactive
                    // (so the tutor can swap); unselected ones are disabled.
                    const isDisabled = !isSelected && selectionFull
                    return (
                        <TouchableOpacity
                            key={sp.id}
                            onPress={() => {
                                if (isDisabled) return
                                onTogglePosition?.(sp.position)
                            }}
                            disabled={isDisabled}
                            activeOpacity={0.7}
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                maxWidth: CHIP_MAX_WIDTH,
                                paddingVertical: 6,
                                paddingHorizontal: 12,
                                borderRadius: 999,
                                borderWidth: 1,
                                opacity: isDisabled ? 0.4 : 1,
                                borderColor: isSelected
                                    ? theme.colors.primary
                                    : theme.colors.light,
                                backgroundColor: isSelected
                                    ? theme.colors.primary
                                    : theme.colors.greyBg,
                            }}
                        >
                            {isSelected && (
                                <FontAwesome5
                                    name="check"
                                    size={10}
                                    color={theme.colors.white}
                                    style={{ marginRight: 6 }}
                                />
                            )}
                            <TextM
                                numberOfLines={2}
                                color={
                                    isSelected
                                        ? theme.colors.white
                                        : theme.colors.grey
                                }
                                fontWeight={isSelected ? "Bold" : "Regular"}
                                fontSize={11}
                                style={{ flexShrink: 1 }}
                            >
                                {sp.name}
                            </TextM>
                        </TouchableOpacity>
                    )
                })}
                {showToggle && (
                    <TouchableOpacity
                        onPress={() => setExpanded((prev) => !prev)}
                        activeOpacity={0.7}
                        style={{
                            paddingVertical: 6,
                            paddingHorizontal: 12,
                        }}
                    >
                        <TextM
                            color={theme.colors.primary}
                            fontWeight="Medium"
                            fontSize={12}
                        >
                            {isExpanded
                                ? "Voir moins"
                                : `Voir plus (${hiddenCount})`}
                        </TextM>
                    </TouchableOpacity>
                )}
            </View>
        </View>
    )
}
